<template>
    <!-- MYAC-917 — the "chat everywhere" surface: one MAC panel, mounted in
         HomeView ABOVE the keyed router-view so the conversation survives
         navigation. Three responsive REGIMES (not user-facing modes) driven by
         measured content width — see stores/chatPanel.ts `regime` getter. -->

    <!-- Docked regime: flex sibling of <main>, pushes content instead of covering it. -->
    <!-- `zIndex` while a dialog shares the row: the panel is in normal flow and
         a modal mask is fixed on the body at 1100 (`@primevue/core/config`,
         `zIndex.modal`), so without lifting it the mask covers the chat and the
         user can see it without touching it. Nothing on the panel's ancestor
         chain creates a stacking context — verified in the browser — so this
         plain `z-index` really does compete with the mask. -->
    <aside v-if="chatPanel.isVisible && chatPanel.regime === 'docked'" ref="dockedRoot"
        class="dock-follow relative shrink-0 border-l border-surface-200 bg-surface-0"
        :style="{
            width: chatPanel.width + 'px',
            zIndex: chatPanel.elevated ? chatPanel.liftZIndex : undefined,
        }">
        <!-- Sticky viewport-fitted column: at page top it sits below the header
             (height shrinks by the header offset); once scrolled it fills 100vh.

             Beside a FULL-SCREEN dialog it fills 100vh at page top too, pulled
             up over the header by the same offset (MYAC-1287). The dialog runs
             edge to edge there, and a panel one header short beside it reads as
             a chat box that shrank (Sy, 2026-09-16). It carries its own surface
             and left border while pulled up, since the part above the `aside`'s
             own box would otherwise show the dimmed header through it. -->
        <div
            class="sticky top-0 flex flex-col"
            :class="{ 'bg-surface-0 border-l border-surface-200': overHeader }"
            :style="columnStyle">
            <div class="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 hover:bg-primary-200"
                :class="{ 'bg-primary-300': isResizing }" @pointerdown="startResize" />
            <div class="px-2 pt-2">
                <ChatPanelToolbar :has-expand="!!expandRoute && !listInPanel" :has-back="conversationsOn && !listInPanel" :title="conversationsOn ? toolbarTitle : ''" :section-title="listInPanel" @back="backToList" @expand="expandToFullPage" @close="closePanel" />
            </div>
            <div class="chat-host flex-1 min-h-0 flex px-2 pb-2">
                <!-- MYAC-1269 — « ‹ » shows the person's conversations HERE, in the panel;
                     the chat stays mounted underneath so its state survives. -->
                <ConversationList v-if="listInPanel" class="flex-1 min-h-0 px-1 pb-2"
                    :current-id="chatbotStore.projectMode ? chatbotStore.sessionId : null"
                    @select="selectFromList" @new="newFromList" />
                <ChatInterface v-show="!listInPanel" class="flex-1" style="height: 100%; min-height: 0;"
                    :opening="scopedOpening" :suggestion-group-keys="suggestionGroupKeys"
                    :opening-group-keys="openingGroupKeys" />
            </div>
        </div>
    </aside>

    <!-- Overlay regime: the panel would crush content below MIN_CONTENT_WIDTH —
         float the same surface over content instead (not a user toggle, a
         degradation of the SAME docked regime). -->
    <Drawer v-else-if="chatPanel.isVisible && chatPanel.regime === 'overlay'" :visible="true" position="right"
        :style="{ width: chatPanel.width + 'px' }" :modal="false" :dismissable="false" :show-close-icon="false"
        :auto-z-index="false" :pt="drawerPt" @update:visible="onDrawerVisible">
        <template #header>
            <ChatPanelToolbar :has-expand="!!expandRoute && !listInPanel" :has-back="conversationsOn && !listInPanel" :title="conversationsOn ? toolbarTitle : ''" :section-title="listInPanel" @back="backToList" @expand="expandToFullPage" @close="closePanel" />
        </template>
        <div class="chat-host h-full flex">
            <!-- MYAC-1269 — « ‹ » shows the person's conversations HERE, in the panel;
                     the chat stays mounted underneath so its state survives. -->
                <ConversationList v-if="listInPanel" class="flex-1 min-h-0 px-1 pb-2"
                    :current-id="chatbotStore.projectMode ? chatbotStore.sessionId : null"
                    @select="selectFromList" @new="newFromList" />
                <ChatInterface v-show="!listInPanel" class="flex-1" style="height: 100%; min-height: 0;"
                :opening="scopedOpening" :suggestion-group-keys="suggestionGroupKeys"
                    :opening-group-keys="openingGroupKeys" />
        </div>
    </Drawer>

    <!-- Takeover regime: viewport too narrow to share space at all — full width. -->
    <Drawer v-else-if="chatPanel.isVisible && chatPanel.regime === 'takeover'" :visible="true" position="right"
        class="!w-full" :modal="false" :dismissable="false" :show-close-icon="false"
        :auto-z-index="false" :pt="drawerPt" @update:visible="onDrawerVisible">
        <template #header>
            <ChatPanelToolbar :has-expand="!!expandRoute && !listInPanel" :has-back="conversationsOn && !listInPanel" :title="conversationsOn ? toolbarTitle : ''" :section-title="listInPanel" @back="backToList" @expand="expandToFullPage" @close="closePanel" />
        </template>
        <div class="chat-host h-full flex">
            <!-- MYAC-1269 — « ‹ » shows the person's conversations HERE, in the panel;
                     the chat stays mounted underneath so its state survives. -->
                <ConversationList v-if="listInPanel" class="flex-1 min-h-0 px-1 pb-2"
                    :current-id="chatbotStore.projectMode ? chatbotStore.sessionId : null"
                    @select="selectFromList" @new="newFromList" />
                <ChatInterface v-show="!listInPanel" class="flex-1" style="height: 100%; min-height: 0;"
                :opening="scopedOpening" :suggestion-group-keys="suggestionGroupKeys"
                    :opening-group-keys="openingGroupKeys" />
        </div>
    </Drawer>

    <!-- Right-edge tab: the panel's spatial home when closed — answers "chat not
         findable" (IR-116 #10/#33).

         Gone on the dedicated chat pages, and that is an EXISTENCE condition,
         not the temporary-state hiding the review lens flags: this tab exists
         only to summon the dock, and on a page that IS the chat there is no
         dock to summon. Same shape as "nothing adopted → no launch button".
         The header's own launcher stays put and explains itself instead. -->
    <!-- Redesign (MYAC-1522, block 3) — the tab carries the assistant's live sign:
         the compass. Working, its needle turns and the tab stays small; it unfolds
         on hover or focus to say « J'y travaille » and what. Done out of sight, it
         unfolds once with « C'est prêt », the subject, « La lire » and « Plus tard »;
         after « Plus tard » it folds back to the compass with its dot. -->
    <!-- MYAC-918 (the mockup, PR 868, Sébastien 2026-10-02) — the handle is on
         every page, the conversation pages included, and it opens the person's
         conversations; the one on screen is continued from there. -->
    <!-- MYAC-1269 — not on the Conversation page: its own column IS the list,
         and the conversation fills the page (the old « Vos conversations »
         drawer is gone; the list lives in the panel everywhere else). -->
    <div v-if="!chatPanel.isVisible && !onConversationPage" data-testid="chat-edge-tab"
        class="edge-tab fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center rounded-l-xl border border-r-0 border-surface-200 bg-surface-0 shadow-md"
        :class="{ 'is-open': edgeTabOpen }"
        @mouseenter="hovering = true" @mouseleave="hovering = false"
        @focusin="hovering = true" @focusout="hovering = false">
        <!-- Unfolds to the LEFT of the compass, which stays against the window's
             edge: a compass that slid away as the tab opened under the cursor
             left the click on « Plus tard » (measured on the mockup). -->
        <div v-if="edgeTabOpen && activityState !== 'idle'" class="flex flex-col items-start gap-1 py-2 pl-4 pr-1 w-[220px] min-w-0" role="status">
            <!-- The compass is the tab stop; the words are a wider target for the mouse. -->
            <button type="button" tabindex="-1" class="flex flex-col text-left leading-tight w-full min-w-0" @click="chatPanel.open()">
                <span class="text-sm font-medium" :class="activityState === 'done' ? 'text-primary' : 'text-surface-800'">
                    {{ activityState === 'working' ? t('chat.activity.at-work') : t('chat.activity.ready-title-short') }}
                </span>
                <span v-if="chatPanel.workingSubject" class="block w-full text-xs text-surface-500 truncate">{{ chatPanel.workingSubject }}</span>
            </button>
            <span v-if="activityState === 'done'" class="flex items-center gap-3 text-sm">
                <button type="button" class="font-medium text-primary hover:underline" data-testid="chat-ready-read"
                    @click="chatPanel.open()">{{ t('chat.activity.read') }}</button>
                <button v-if="chatPanel.readyNoticeOpen" type="button" class="text-surface-500 hover:text-surface-700"
                    data-testid="chat-ready-later" @click="chatPanel.dismissReadyNotice()">{{ t('chat.activity.later') }}</button>
            </span>
        </div>
        <button type="button" class="edge-tab-main flex items-center rounded-l-xl hover:bg-surface-50"
            data-testid="chat-edge-tab-open"
            v-tooltip.left="activityState === 'idle' ? t('chat.panel-launcher-tooltip') : undefined"
            :aria-label="edgeTabLabel" @click="onEdgeTab">
            <ActivityCompass :state="activityState" :size="28" />
        </button>
    </div>


</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import Drawer from 'primevue/drawer'
import ChatInterface from '@/components/chat/ChatInterface.vue'
import ChatPanelToolbar from '@/components/chat/ChatPanelToolbar.vue'
import ConversationList from '@/components/conversations/ConversationList.vue'
import { startNewConversation } from '@/composables/conversationMoves'
import { useConversationsStore, type ConversationRow } from '@/stores/conversations'
import { useFeatureFlagsStore } from '@/stores/featureFlags'
import ActivityCompass from '@/components/chat/ActivityCompass.vue'
import { hasChatOwnedMask, topMaskZIndex } from '@/components/chat/topMaskZIndex'
import { CHAT_PAGE_ROUTES, isConversationPage, useChatPanelStore } from '@/stores/chatPanel'
import { isPilotageRoute, pilotageLocation } from '@/utils/pilotageRoutes'
import { dockedColumnStyle, floatingMaskBounds, reachesOverHeader } from './dockedColumnLayout'
import { besideMaskStyle, isBesideChat, syncBesideChat } from '@/components/shared/dialog/chatBesideLayout'
import { useChatbotStore } from '@/stores/chatbot'
import { useChatScope } from '@/composables/useChatScope'
import { useScopedOpening } from '@/composables/useScopedOpening'
import { useProjectsStore } from '@/stores/projects'
import { CHAT_SUGGESTION_GROUPS, chatPageSuggestionProps } from '@/constants/chatSuggestions'
import { PROMPT_ROUTES } from '@/router/savedPromptRoutes'

const { t } = useI18n()
const chatPanel = useChatPanelStore()
/** Redesign — the live sign on the edge tab (composables/useChatActivity.ts). */
const hovering = ref(false)
/** Unfolded: on hover/focus, or while a fresh « C'est prêt » waits for its answer. */
const edgeTabOpen = computed(() => hovering.value || (chatPanel.replyUnseen && chatPanel.readyNoticeOpen))
// MYAC-918 — the handle speaks for every conversation of the person on this
// project: one still being written turns the needle, an answer they have not
// seen leaves the dot.
const conversations = useConversationsStore()
const featureFlags = useFeatureFlagsStore()
// MYAC-1269: a shared conversation's news counts too (`listed` = mine + shared)
const anyWorking = computed(() => conversations.listed.some(r => conversations.stateOf(r) === 'working'))
const anyUnread = computed(() => conversations.listed.some(r => conversations.stateOf(r) === 'answer'))
const activityState = computed<'idle' | 'working' | 'done'>(() =>
    chatPanel.isWorking || anyWorking.value ? 'working'
        : chatPanel.replyUnseen || anyUnread.value ? 'done' : 'idle')
const edgeTabLabel = computed(() =>
    activityState.value === 'working' ? t('chat.activity.working-aria', { subject: chatPanel.workingSubject })
        : activityState.value === 'done' ? t('chat.activity.done-aria')
            : t('chat.panel-launcher-tooltip'))

/**
 * Lift the panel over a dialog's mask, in EVERY regime.
 *
 * The docked regime is an `<aside>` in normal flow and takes a plain z-index.
 * The other two are `Drawer`s, whose own overlay z-index sits BELOW a modal
 * mask's 1100 — so the first version of this fix, which only touched the
 * `<aside>`, left the chat covered on any viewport narrow enough to leave the
 * docked regime.
 *
 * On a Drawer the z-index belongs to its MASK — the fixed, click-through layer
 * that holds the panel and is the panel's stacking context — so a z-index on
 * the root competed only inside it. `autoZIndex` is off on both Drawers so the
 * mask takes `floatingZIndex` and nothing else: lifted while a dialog is up,
 * under the app's popups otherwise (MYAC-1484 — see FLOATING_PANEL_Z_INDEX).
 */
/**
 * Watch the body for a modal mask, and lift the panel while one is there.
 *
 * PrimeVue appends every dialog mask as a direct child of `<body>`, so one
 * `childList` observer on the body sees all of them — including the fifteen
 * dialogs that know nothing about this panel (MYAC-1134). `subtree` stays off
 * on purpose: it would fire on every DOM mutation in the app to learn nothing
 * more.
 */
let maskObserver: MutationObserver | null = null

onMounted(() => {
  const sync = () => {
    chatPanel.setMaskZIndex(topMaskZIndex())
    chatPanel.setChatOwnedMask(hasChatOwnedMask())
  }
  maskObserver = new MutationObserver(sync)
  maskObserver.observe(document.body, { childList: true })
  sync()
})

/**
 * The other half of the lift — MYAC-1541.
 *
 * A panel painted over every mask covers whatever dialog sits under it, so the
 * dialogs have to move: the panel says how much room it takes and the
 * stylesheet narrows every page mask by that much (assets/main.css). It used to
 * be the shared dialog shell's job, which left every dialog outside the shell
 * with its right side under the panel and its buttons out of reach.
 */
watchEffect(() => {
  syncBesideChat(
    document.documentElement,
    isBesideChat(chatPanel.isVisible, chatPanel.regime)
      ? besideMaskStyle(chatPanel.width).paddingRight
      : null,
  )
})

onBeforeUnmount(() => {
  maskObserver?.disconnect()
  maskObserver = null
  chatPanel.setMaskZIndex(0)
  chatPanel.setChatOwnedMask(false)
  syncBesideChat(document.documentElement, null)
  // The hold is this component's reading of the route — it must not outlive it.
  chatPanel.setSuppressed(false)
})

const chatbotStore = useChatbotStore()
const projectsStore = useProjectsStore()
const route = useRoute()
const router = useRouter()

// "Expand" opens the full chat page of the project the CHAT is scoped to —
// same conversation, same store, just more room. In a project that is the
// project itself (unchanged); on the dashboard it is the QMS the chat resolved
// to (MYAC-1327), so expanding lands on the same session, not on whatever
// project happened to be visited last. Hidden when the chat has no scope.
const chatScope = useChatScope()
// MYAC-1327 — whenever the chat's subject is a QMS, the dock opens on that QMS's
// Pilotage message: it is "a second window for the same thing" (Arslene,
// 2026-09-16). The chatbot store is a singleton, so the dock and the Pilotage tab
// already share ONE conversation — a message sent in either shows in both. This
// only gives the dock the same greeting. A Product keeps its own surface's
// opening, and an unattached chat has no project whose opening to show.
const scopedOpening = useScopedOpening(
    () => (chatScope.scopeProject.value?.type === 'qms'
        ? chatScope.scopeProjectId.value
        : null))
const expandRoute = computed(() => {
    const project = chatScope.scopeProject.value
    if (!project) return null
    // Redesign (MYAC-1522, block 3): the full-page conversation is Pilotage's (the
    // letter), the same session.
    if (project.type !== 'qms' && project.type !== 'td') return null
    return pilotageLocation(project.type, project.id, 'chat')
})

const expandToFullPage = () => {
    if (!expandRoute.value) return
    // The old chat page holds the dock off by itself (MYAC-1156) and keeps it open
    // for the way back; the Pilotage conversation closes it on arrival (watcher
    // below): the conversation moves to the page, it is not duplicated.
    router.push(expandRoute.value)
}

// The overlay/takeover Drawer manages its own visibility events (Escape,
// outside interactions); mirror any close back into the store so the
// launcher/shortcut state stays truthful.
const onDrawerVisible = (visible: boolean) => {
    if (!visible) chatPanel.close()
}

// MYAC-1156 — the dock is HELD OFF the dedicated chat pages rather than closed
// on arrival. Arriving must not pop a second chat over the one filling the page,
// and the member's own open/closed choice must survive the visit untouched — a
// `close()` here would persist, quietly forgetting a dock they had chosen to
// keep open. The hold lifts the moment they navigate away.
//
// Leaving with a live conversation also OPENS it (Aymeric, 2026-08-11): the chat
// page is the expanded view of the same session, so walking away hands the
// thread back to the dock rather than dropping it — including for a member who
// had never opened the dock, since the conversation started on the page.
//
// Redesign (MYAC-1522, block 3) — Pilotage's conversation tab IS the full-page
// conversation too (isConversationPage). Arriving there CLOSES the dock
// (persisted) instead of holding it off: Pilotage is where every project lands,
// and a merely held-off dock reappeared on the next page every time (Sébastien,
// 2026-10-01: Tâches with the dock open → Conversation → L'essentiel = dock
// back). Leaving it with a conversation under way hands the thread to the dock,
// as the chat page does, wherever the dock can sit beside the page (Aymeric,
// 2026-10-06: « si une conversation est en cours, le chat passe en latéral »).
// No conversation, no dock: a fresh session or a page read without asking
// anything leaves it closed, so landing on Pilotage does not open it on every
// navigation. Never as an overlay: it would cover the page just opened.
watch(() => ({ name: String(route.name ?? ''), onConversation: isConversationPage(route) }), (now, old) => {
    const { name, onConversation: onChatPage } = now
    const wasChatPage = CHAT_PAGE_ROUTES.includes(old?.name ?? '')
    const wasConversation = !wasChatPage && !!old?.onConversation
    if (onChatPage && !CHAT_PAGE_ROUTES.includes(name)) chatPanel.close()
    // Before the re-open below, so leaving is not swallowed by its own hold.
    chatPanel.setSuppressed(onChatPage)
    const handsBack = wasChatPage || (wasConversation && chatPanel.regime === 'docked')
    if (handsBack && !onChatPage && chatbotStore.messages.length > 0) {
        // Fix-wave (whole-branch review, C2): this re-open is a navigation side
        // effect (handing the conversation back to the dock), not a user gesture —
        // ROUTE_ACTIONS already counted `chat_opened` for arriving at the chat page,
        // so counting this too double-counted every visit.
        chatPanel.open({ userInitiated: false })
    }
}, { immediate: true })

// --- Page-context collector (MYAC-919 foundation) ---------------------------
// Kept current on every navigation — see the doctrine comment on
// ChatPageContext in stores/chatPanel.ts (UX input only, never authorization).

// MYAC-1327 — the conversation carries the scope.
//
// This replaces "walking in connects the chat": visiting a project used to write a
// remembered scope, which is how a QA fixture project ended up selected in front of
// 23 real ones. A visit tells the resolver where the member IS (setRouteProjectId,
// above); only a conversation gives the dashboard a subject.
//
// A chat started inside a project keeps that project as its subject when the member
// walks out to the dashboard: the thread is still on screen, so the header must
// still name what it is about. The moment there is no conversation, the scope goes
// with it and the header asks again — a scope that outlives its conversation is how
// a QA fixture project ended up selected in front of 23 real ones.
//
// Pushed from here rather than read inside the composable: `stores/chatbot` already
// imports `useChatScope`, and importing it back would close the loop.
watch(
    [() => chatbotStore.projectId, () => chatbotStore.events.length],
    () => {
        chatScope.setLiveConversationProject(
            chatbotStore.events.length > 0 ? chatbotStore.projectId : null)
    },
    { immediate: true },
)

// ── MYAC-918 — the person's conversations: the handle's list, the dock's title ──
const routeProjectId = computed(() => {
    const pid = route.params.pid
    return typeof pid === 'string' && pid ? pid : null
})
// Bound to the page's project: the handle's sign reads the rows, the list and
// the sidebar read the same store.
// Behind the `enable_conversations` flag (a release flag, company-overridable):
// off, nothing subscribes — the handle opens the chat as before MYAC-918.
const conversationsOn = computed(() => featureFlags.isConversationsEnabled)
watch([routeProjectId, conversationsOn], ([pid, on]) => {
    if (on) conversations.bind(pid)
    else { conversations.unbind(); chatPanel.closeList() }
}, { immediate: true })
// MYAC-1269 — the list lives in the panel, the only place it opens outside the
// Conversation page (whose own column is the list).
const listInPanel = computed(() => conversationsOn.value && chatPanel.isVisible && chatPanel.listOpen)
const onConversationPage = computed(() => isConversationPage(route))
// The panel closing by any path (its ×, the header's assistant button, arriving on
// the Conversation page) takes its list with it.
watch(() => chatPanel.isVisible, (visible) => { if (!visible) chatPanel.closeList() })
const toolbarTitle = computed(() => {
    if (listInPanel.value) return t('conversations.sidebar.title')
    if (!chatbotStore.projectMode || !chatbotStore.sessionId) return ''
    return chatbotStore.chatRow?.title
        || (chatbotStore.messages.length ? t('conversations.untitled') : t('conversations.new'))
})
const backToList = () => chatPanel.openList()
const closePanel = () => {
    chatPanel.closeList()
    chatPanel.close()
}
/** The handle: the panel on the conversation under way, or on the list when there is none. */
const onEdgeTab = () => {
    if (!conversationsOn.value) { chatPanel.open(); return }
    const hasConversation = chatbotStore.projectMode && !!chatbotStore.sessionId && chatbotStore.events.length > 0
    if (hasConversation) chatPanel.closeList()
    else chatPanel.openList()
    chatPanel.open({ userInitiated: true })
}
/** A row of the panel's list: the conversation opens in the panel, the page stays. */
const selectFromList = (row: ConversationRow) => {
    chatPanel.closeList()
    if (chatbotStore.projectMode && chatbotStore.sessionId === row.id) return
    chatbotStore.openConversation(row.projectId, row.id)
}
const newFromList = async () => {
    chatPanel.closeList()
    await startNewConversation()
}

watch(() => route.fullPath, () => {
    // MYAC-1327 — the chat-scope resolver's ONE source for "am I inside a
    // project": the route's own pid, never the sticky currentProjectId.
    const pid = route.params.pid
    chatScope.setRouteProjectId(typeof pid === 'string' && pid ? pid : null)
    const project = projectsStore.currentProject
    chatPanel.setPageContext({
        routeName: String(route.name ?? ''),
        projectId: project?.id ?? null,
        projectType: project?.type === 'qms' ? 'qms' : project?.type === 'td' ? 'td' : null,
        tab: typeof route.query.tab === 'string' ? route.query.tab : null,
        params: Object.fromEntries(Object.entries(route.params).map(([k, v]) => [k, String(v)])),
    })
}, { immediate: true })

// Page-aware suggestion ordering: a page whose subject matches a suggestion group
// leads with that group; everywhere else keeps the default order.
//
// MYAC-1013 — the registers page is the second such pairing. The agent has reached
// all four registers from any surface for a while (grounding + get_qms_register +
// the gated refresh/append/delete actions), so this is purely about DISCOVERY:
// standing on the Registers tab, the docked panel should open on what it can do
// with registers rather than on document search.
const LEAD_GROUP_BY_ROUTE: { match: (routeName: string) => boolean; group: string }[] = [
    // The Pilotage pages: their own addresses since MYAC-1522 (`*-pilotage-<section>`).
    { match: (r) => r.includes('referentials') || isPilotageRoute(r), group: 'requirements_assessment' },
    { match: (r) => r.includes('registers'), group: 'registers' },
]

// MYAC-1329 — the saved-prompts pages hang off a chat page but show this dock in
// place of that page's own chat, so the dock opens on the families the chat page
// would have. The route name says which tree; no need to wait for the project.
const promptsPageSuggestions = computed(() => {
    const routeName = chatPanel.pageContext?.routeName
    const tree = (Object.keys(PROMPT_ROUTES) as (keyof typeof PROMPT_ROUTES)[]).find(
        (t) => routeName === PROMPT_ROUTES[t].list || routeName === PROMPT_ROUTES[t].detail)
    return tree ? chatPageSuggestionProps(tree) : null
})

const suggestionGroupKeys = computed<string[] | undefined>(() => {
    if (promptsPageSuggestions.value) return promptsPageSuggestions.value.suggestionGroupKeys
    const routeName = chatPanel.pageContext?.routeName
    if (!routeName) return undefined
    const lead = LEAD_GROUP_BY_ROUTE.find((entry) => entry.match(routeName))?.group
    if (!lead) return undefined
    return [lead, ...CHAT_SUGGESTION_GROUPS.map(g => g.key).filter(k => k !== lead)]
})
const openingGroupKeys = computed(() => promptsPageSuggestions.value?.openingGroupKeys)

// --- Docked mode: viewport fitting -----------------------------------------
// The sticky column's height must account for whatever sits above the panel
// (header + banners) while it hasn't stuck yet. Measured, not hardcoded —
// banner presence varies.
const dockedRoot = ref<HTMLElement | null>(null)
const dockedTopOffset = ref(0)

// The two rules live in `dockedColumnLayout` — see that module for why.
const overHeader = computed(() => reachesOverHeader(dockedTopOffset.value, chatPanel.hasFullScreenDialog))

const columnStyle = computed(() =>
    dockedColumnStyle(dockedTopOffset.value, chatPanel.hasFullScreenDialog),
)

const updateDockedOffset = () => {
    const el = dockedRoot.value
    if (!el) return
    dockedTopOffset.value = Math.max(0, el.getBoundingClientRect().top)
}

// --- Floating mode: where the Drawer starts (MYAC-1484) ----------------------
// The Drawer renders nothing in the layout row, so the line is read off the row
// itself: its top is exactly where the docked column would start.
const floatingTopOffset = ref(0)

const updateFloatingOffset = () => {
    const el = document.getElementById('home-layout')
    if (!el) return
    floatingTopOffset.value = Math.max(0, el.getBoundingClientRect().top)
}

const updateOffsets = () => {
    updateDockedOffset()
    updateFloatingOffset()
}

const drawerPt = computed(() => ({
    mask: {
        style: {
            zIndex: chatPanel.floatingZIndex,
            ...floatingMaskBounds(floatingTopOffset.value, chatPanel.hasFullScreenDialog),
        },
    },
}))

watch([() => chatPanel.isVisible, () => chatPanel.regime], () => {
    requestAnimationFrame(updateOffsets)
}, { immediate: true })

// --- Responsive regimes: measure content width ------------------------------
// Observes the HomeView flex row (content + panel when docked) — see
// HomeView.vue's #home-layout id. layoutWidth minus the panel's own width is
// the content remainder the `regime` getter checks against MIN_CONTENT_WIDTH.
let layoutObserver: ResizeObserver | null = null

const observeLayout = () => {
    const layoutEl = document.getElementById('home-layout')
    if (!layoutEl) return
    layoutObserver = new ResizeObserver(() => {
        chatPanel.setLayoutWidth(layoutEl.clientWidth)
        updateOffsets()
    })
    layoutObserver.observe(layoutEl)
    chatPanel.setLayoutWidth(layoutEl.clientWidth)
}

// --- Docked mode: resize handle --------------------------------------------
const isResizing = ref(false)
let resizeStartX = 0
let resizeStartWidth = 0

const startResize = (event: PointerEvent) => {
    isResizing.value = true
    resizeStartX = event.clientX
    resizeStartWidth = chatPanel.width
    window.addEventListener('pointermove', onResizeMove)
    window.addEventListener('pointerup', stopResize)
}

const onResizeMove = (event: PointerEvent) => {
    // Panel is on the right: dragging left grows it. Transient (not persisted)
    // for performance — persisting happens once in stopResize.
    // No explicit drag cap: dragging past layoutWidth - MIN_CONTENT_WIDTH makes
    // the store's `regime` getter flip docked -> overlay on its own, so the
    // panel floats instead of crushing content — the spec's resize guarantee.
    chatPanel.setWidthTransient(resizeStartWidth + (resizeStartX - event.clientX))
}

const stopResize = () => {
    isResizing.value = false
    chatPanel.setWidth(chatPanel.width)
    window.removeEventListener('pointermove', onResizeMove)
    window.removeEventListener('pointerup', stopResize)
}

// --- Global shortcuts: Ctrl/Cmd+J toggles; Escape closes when focus is
// inside the docked panel (focus-scoped so it never fights an open dialog).
// The overlay/takeover Drawer handles its own Escape and mirrors through
// onDrawerVisible.
const onKeydown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey
        && event.key.toLowerCase() === 'j') {
        event.preventDefault()
        chatPanel.toggle()
        return
    }
    if (event.key === 'Escape' && chatPanel.isVisible && dockedRoot.value?.contains(document.activeElement)) {
        chatPanel.close()
    }
}

onMounted(() => {
    window.addEventListener('keydown', onKeydown)
    window.addEventListener('scroll', updateOffsets, { passive: true })
    window.addEventListener('resize', updateOffsets)
    observeLayout()
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown)
    window.removeEventListener('scroll', updateOffsets)
    window.removeEventListener('resize', updateOffsets)
    layoutObserver?.disconnect()
    stopResize()
})
</script>

<style scoped>
/* MYAC-1269 — the panel's toolbar already opens the column: the chat card's own
   top padding only widened the gap under the conversation's title. */
.chat-host :deep(.p-card-body) { padding-top: 0.25rem; }
.edge-tab-main { padding: 10px; }
.edge-tab { transition: box-shadow 0.2s ease-out; }
/* Redesign — the dock slides in from the right when it opens: arriving on a
   page from the conversation, the chat visibly follows. */
.dock-follow { animation: dock-follow 0.45s cubic-bezier(.22,.61,.36,1) both; }
@keyframes dock-follow { from { transform: translateX(40%); opacity: 0; } to { transform: none; opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .dock-follow { animation: none; } }

/* The panel shell (aside border / Drawer) already provides the frame —
   ChatInterface's own Card must not draw a second one inside it. */
.chat-host :deep(.p-card) {
    border: 0;
    box-shadow: none;
    border-radius: 0;
    background: transparent;
}

/* ChatInterface's Card needs the same flex height contract its page mounts
   provide (see QMSChatView) — without it the panel collapses to content size. */
.chat-host :deep(.p-card-body) {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    height: 100%;
}

.chat-host :deep(.p-card-content) {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    height: 100%;
}
</style>
